/**
 * CrownPointsBanner — booking-funnel insert that reminds signed-in
 * clients of their Crown Points balance and lets them apply some toward
 * the booking with a single tap.
 *
 * UX rules:
 *   • Silent for guests (no client_token in localStorage) — we never
 *     leak someone else's balance based on a typed email.
 *   • Silent for clients with < 50 points (the minimum redemption).
 *   • Three tap-target choices: 500-pt tier, 1000-pt tier, or "use all".
 *     Each shows the live $ value at the client's current tier rate
 *     (computed server-side in /api/client/points-summary).
 *   • Selection flows up via `onChange({points, dollar_value})` so the
 *     parent funnel can call /api/client/redeem at confirm time.
 *
 * The dollar values are PREVIEWS — the actual discount code is created
 * only when the booking is confirmed, so the client never accidentally
 * burns points by clicking around.
 */
import { useEffect, useState } from "react";
import { pointsToDollars as _canonicalPointsToDollars } from "@/lib/crownPoints";
import axios from "axios";
import { Crown, Sparkles, Check } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// Iter 383 · Feb 14 2026 · Karine bug: multiple hand-rolled point→$ formulas
// were producing conflicting values on the same client screen ($73.44 vs
// $61.20 for 1,224 pts). Everything now routes through the SINGLE canonical
// `pointsToDollars()` in @/lib/crownPoints — which mirrors the backend
// `_marginal_dollars()` — so what the client sees == what they get charged.
// The old flat rateForLifetime() lookup remains only for the fixed-tier
// preset labels (500 pts, 1000 pts, etc.) where displaying "at Silver rate"
// is still helpful; but the dollar total below is always the canonical.
function rateForLifetime(lifetimePts) {
  if (lifetimePts >= 4001) return 0.09;   // Diamond
  if (lifetimePts >= 2501) return 0.07;   // Gold
  if (lifetimePts >= 1501) return 0.06;   // Silver
  return 0.05;                            // Bronze
}

// Suggested redemption options the banner offers based on the client's
// available balance. We only ever show tiers the client can actually
// redeem RIGHT NOW; under-balance tiers are filtered out. The "use all"
// option appears whenever it's strictly better than the highest fixed tier.
function buildOptions(available, lifetime, signupBonusCredit) {
  const dollarFor = (pts) => {
    // Signup-bonus special case: first 1000 pts convert to a flat $60 if
    // the bonus credit hasn't been claimed yet. Everything above the bonus
    // uses the CANONICAL marginal ladder.
    if (signupBonusCredit > 0 && pts >= 1000) {
      const remainder = pts - 1000;
      return Math.round((60 + _canonicalPointsToDollars(remainder)) * 100) / 100;
    }
    return _canonicalPointsToDollars(pts);
  };
  const fixed = [500, 1000, 2000, 3500, 5000].filter((p) => p <= available);
  const opts = fixed.map((p) => ({ points: p, dollar: dollarFor(p), label: `Use ${p.toLocaleString()} pts` }));
  // Add a "use all" CTA when the balance exceeds the largest tier shown.
  const largest = fixed[fixed.length - 1] || 0;
  if (available > largest && available >= 50) {
    opts.push({ points: available, dollar: dollarFor(available), label: `Use all ${available.toLocaleString()} pts`, isAll: true });
  }
  return opts;
}

export default function CrownPointsBanner({ value, onChange }) {
  const [summary, setSummary] = useState(null);   // null = loading, false = no auth/skip
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    const token = typeof window !== "undefined"
      ? localStorage.getItem("client_token")
      : null;
    if (!token) {
      setSummary(false);
      setBusy(false);
      return;
    }
    (async () => {
      try {
        const r = await axios.get(`${API}/api/client/points-summary`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 8000,
        });
        setSummary(r.data || false);
      } catch {
        setSummary(false);
      } finally {
        setBusy(false);
      }
    })();
  }, []);

  if (busy) return null;
  if (!summary) return null;

  // Server returns: { available_points, lifetime_points, signup_bonus_credit, first_name, ... }
  const available = Number(summary.available_points ?? summary.points ?? 0);
  const lifetime  = Number(summary.lifetime_points ?? available);
  const signupBonusCredit = Number(summary.signup_bonus_credit ?? 0);
  const firstName = summary.first_name || "";

  // Below the 50-pt minimum — encourage them gently instead of redeeming.
  if (available < 50) {
    const toMin = 50 - available;
    return (
      <div
        className="rounded-2xl border border-amber-200 bg-amber-50/60 px-4 py-3 mb-4 flex items-center gap-3"
        data-testid="crown-points-banner-under"
      >
        <Crown className="w-5 h-5 text-amber-500 flex-shrink-0" />
        <p className="text-sm text-amber-900 leading-snug">
          {firstName ? `Hi ${firstName}! ` : ""}You&apos;re <b>{toMin} pts away</b> from your first Crown redemption. Book to earn more.
        </p>
      </div>
    );
  }

  const opts = buildOptions(available, lifetime, signupBonusCredit);

  return (
    <div
      className="rounded-2xl border-2 border-amber-400/60 bg-gradient-to-br from-amber-50 via-amber-50 to-white px-4 py-4 mb-5 shadow-sm"
      data-testid="crown-points-banner"
    >
      <div className="flex items-center gap-2 mb-2">
        <Crown className="w-5 h-5 text-amber-600 flex-shrink-0" />
        <h3 className="text-[15px] font-bold text-amber-900" style={{ fontFamily: "'Playfair Display', serif" }}>
          {firstName ? `${firstName}, ` : ""}you have <span className="tabular-nums">{available.toLocaleString()}</span> Crown Points
        </h3>
      </div>
      <p className="text-[12px] text-amber-800 mb-3 leading-snug">
        Apply some toward today&apos;s booking — tap one of the options below. We&apos;ll deduct the points and discount your treatment automatically at checkout.
      </p>

      <div className="space-y-2">
        {/* "No thanks" option always available so the choice is reversible. */}
        <button
          type="button"
          onClick={() => onChange?.(null)}
          className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 min-h-[48px] rounded-lg border-2 transition-colors ${
            !value
              ? "border-amber-600 bg-white"
              : "border-amber-200 bg-white/60 hover:border-amber-300"
          }`}
          data-testid="crown-points-opt-none"
        >
          <span className="flex items-center gap-2 text-sm font-medium text-amber-900">
            <span className={`w-5 h-5 rounded border-2 flex items-center justify-center ${!value ? "border-amber-600 bg-amber-600" : "border-amber-400"}`}>
              {!value && <Check className="w-3.5 h-3.5 text-white" />}
            </span>
            Save my points for next time
          </span>
        </button>

        {opts.map((opt) => {
          const selected = value && value.points === opt.points;
          return (
            <button
              type="button"
              key={opt.points + (opt.isAll ? "-all" : "")}
              onClick={() => onChange?.({ points: opt.points, dollar_value: opt.dollar })}
              className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 min-h-[48px] rounded-lg border-2 transition-colors ${
                selected
                  ? "border-amber-600 bg-amber-100/70"
                  : "border-amber-200 bg-white hover:border-amber-300"
              }`}
              data-testid={`crown-points-opt-${opt.points}`}
            >
              <span className="flex items-center gap-2 text-sm font-medium text-amber-900 min-w-0">
                <span className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${selected ? "border-amber-600 bg-amber-600" : "border-amber-400"}`}>
                  {selected && <Check className="w-3.5 h-3.5 text-white" />}
                </span>
                <span className="truncate">{opt.label}</span>
                {opt.isAll && (
                  <Sparkles className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
                )}
              </span>
              <span className="text-sm font-bold text-emerald-700 tabular-nums whitespace-nowrap">
                −${opt.dollar.toFixed(2)}
              </span>
            </button>
          );
        })}
      </div>

      {value && (
        <div
          className="mt-3 px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-[12px] text-emerald-800"
          data-testid="crown-points-confirmation"
        >
          ✨ <b>${value.dollar_value.toFixed(2)} off</b> will be applied when you confirm this booking.
          You&apos;ll have <b>{(available - value.points).toLocaleString()} pts</b> left after this visit.
        </div>
      )}
    </div>
  );
}
